import type React from 'react';

import { Text } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { BlurredBackdrop } from '../BlurredBackdrop';
import { CreatorBadge } from '../CreatorBadge';
import { TikTokPlayer } from '../TikTokPlayer';

/**
 * Mobile video-first hero (design V10): a black hero filled with a blurred copy of the
 * video's cover frame, carrying the creator badge, title and signature tally above the
 * 9:16 embed.
 */
export const VideoFirstHeroMobile: React.FC<Props> = ({
	coverUrl,
	creatorName,
	playerUrl,
	signatureCount,
	signatureGoal,
	title,
	titleDataQa,
}) => {
	const { translatePlural } = useI18n();

	return (
		<div
			className="text-typography-inverse-strong-fixed relative flex flex-col items-center gap-6 overflow-hidden bg-black pt-8 pb-16 md:hidden"
			data-qa="video-first-hero"
		>
			<BlurredBackdrop coverUrl={coverUrl} />

			<div className="relative flex w-full flex-col items-start gap-4 px-8">
				<CreatorBadge name={creatorName} tone="on-dark" />

				<div className="flex w-full flex-col items-start gap-2">
					<Text
						as="h1"
						variant="heading"
						size="small"
						fontWeight="bold"
						data-qa={titleDataQa}
						className="text-wrap wrap-anywhere"
					>
						{title}
					</Text>
					<div className="flex items-center gap-1">
						<Text variant="body" size="x-small" fontWeight="bold" className="whitespace-nowrap">
							{translatePlural('corgi.petition_details.video_first_hero.signature_count', signatureCount.displayed, {
								countLocalized: signatureCount.displayedLocalized,
							})}
						</Text>
						<Text variant="body" size="x-small" className="whitespace-nowrap opacity-80">
							<Translate
								value="corgi.petition_details.video_first_hero.signature_goal"
								replacements={{ goalLocalized: signatureGoal.displayedLocalized }}
							/>
						</Text>
					</div>
				</div>
			</div>

			{/* 326x580 in the design; the 4rem inset matches the px-8 of the text block above */}
			<div className="relative aspect-[9/16] w-[calc(100%-4rem)] max-w-[326px] overflow-hidden rounded-lg">
				{playerUrl && <TikTokPlayer playerUrl={playerUrl} title={title} viewport="mobile" />}
			</div>
		</div>
	);
};

type SignatureTally = Readonly<{ displayed: number; displayedLocalized: string }>;

type Props = {
	coverUrl: string | undefined;
	creatorName: string | undefined;
	playerUrl: string | undefined;
	signatureCount: SignatureTally;
	signatureGoal: SignatureTally;
	title: string;
	titleDataQa: string | undefined;
};
